import { useForm, validators } from "@hooks";
import classNames from "classnames";
import currency from "currency.js";
import { Constants, helpers, Enums } from "@shared";
import { Button, MoneyInput, PercentInput, TextField } from "@common";
import type { Limits } from '@app-types';

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
    ? Omit<T, K>
    : never;

type PayorFormInput = {
    idPayor: number;
    email: string;
    fullName: string;
    payAmount: number | null;
    payPercentage: number | null;
    dueDay: number | null;
    amountType: number | null;
    idInvitation?: string; // Regurgitated to construct an enrollment payor
    isEnrolled?: boolean;  // Used in this module
    paid?: number;         // Regurgitated to construct an enrollment payor
} & ({
    alreadySelected: true;
} | {
    alreadySelected: false;
    // These "is*" properties are used to reconstruct an account payor when
    // setting an allocation for the first time as 0 (the no-op case):
    isActive: boolean;
    isEnrolled: boolean;
    isGuarantor: boolean;
});

// The values come in as numbers or null, but historically we "normalize" the
// values to these various types when handling them internally:
type PayorFormValues = DistributiveOmit<PayorFormInput, `amountType` | `payAmount` | `payPercentage`> & {
    amountType: number;
    payAmount: string | number;
    payPercentage: string;
};

// The result is normalized back to numbers for sanity:
type PayorFormResult = DistributiveOmit<PayorFormValues, `payAmount` | `payPercentage`> & {
    payAmount: number;
    payPercentage: number;
};

interface UpsertPayorProps {
    sunboundTotal: number;
    upsert: (values: PayorFormResult) => void;
    cancelEditing: () => void;
    payor: PayorFormInput;
    limits: Limits;
}

const UpsertPayor = ({ sunboundTotal, upsert, payor, cancelEditing, limits }: UpsertPayorProps) => {

    const validateAmount = (val: currency.Any, vals: PayorFormValues) => {
        const amount = currency(val);
        const paid = typeof vals.paid === `number` ? currency(vals.paid) : null;
        const min = currency(limits.min);
        const max = currency(limits.max);

        // Check if payor has already paid - this must be checked before allowing 0
        if (paid && amount.value < paid.value) {
            return { "payAmount": `Payor has already paid ${paid.format()}` };
        }

        // Allow exactly 0 to unset split (only if no payments have been made)
        if (amount.value === 0) {
            return true;
        }

        if (amount.value > max.value) {
            return { "payAmount": `Max payment amount is ${max.format()}` };
        }

        if (amount.value < Constants.System.MIN_PAYMENT_AMOUNT) {
            return { "payAmount": `Min payment amount is ${min.format()}` };
        }

        if (max.subtract(amount).intValue !== 0 && max.subtract(amount).value < currency(Constants.System.MIN_PAYMENT_AMOUNT).value) {
            return { "payAmount": `Guarantor payment is less than ${currency(Constants.System.MIN_PAYMENT_AMOUNT).format()}` };
        }

        return true;
    };

    const validatePercentage = (val: string) => {
        const re = /^\d{1,3}(\.\d{1,2})?$/;
        if (!re.test(val)) {
            return false;
        }
        const amount = currency(val);
        // Allow 0 to 100, where 0 unsets the split
        return amount.value >= 0 && amount.value <= 100;
    };

    const validations = {
        fullName: (val: string) => validators.hasValue(val) || { fullName: `Full name is required` },
        payPercentage: (val: string) => validatePercentage(val) || { payPercentage: `Invalid percentage` },
        payAmount: (val: string | number, vals: PayorFormValues) => validateAmount(val, vals) || { payAmount: `Invalid amount` }
    };

    const handleSubmit = (values: PayorFormValues) => {
        upsert({
            ...values,
            payAmount: currency(values.payAmount).value,
            payPercentage: currency(String(values.payPercentage), { precision: 4 }).divide(100).value
        });
    };

    const { values, errors, changeHandler, submitHandler, customUpdate } = useForm(
        {
            ...payor,
            // Ignore payAmount and payPercentage values from previous
            // enrollments.  The values might not be synchronized, which
            // could cause bugs; and the fields should be blank anyway.
            ...(payor.isEnrolled === false
                ? { payAmount: ``, payPercentage: ``, amountType: Enums.AmountType.Percentage }
                : { payAmount: payor.payAmount ?? ``, payPercentage: helpers.printPercentage(payor.payPercentage), amountType: payor.amountType ?? Enums.AmountType.Percentage }
            )
        },
        {},
        validations,
        handleSubmit
    );

    const changeAmountProxy = ({ target: { value, name } }: { target: { value: string; name: string } }) => {
        let payAmount: string | number = ``;
        let payPercentage = ``;
        let amountType: number;
        const re = /^[\d.]*$/;

        if (value !== `` && !re.test(value)) {
            return;
        }

        if (name === `payAmount`) {
            payAmount = value;
            if (value !== ``) {
                payPercentage = helpers.printPercentage(helpers.toFixedDown(helpers.percentage(value, sunboundTotal), 4));
            }
            amountType = Enums.AmountType.Amount;
        } else {
            payPercentage = value;
            if (value !== ``) {
                payAmount = helpers.toFixedDown(helpers.amount(value, sunboundTotal), 2);
            }
            amountType = Enums.AmountType.Percentage;
        }

        customUpdate({ payAmount, payPercentage, amountType });
    };

    const amountTypeChangeHandler = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        customUpdate({ amountType: value === `payPercentage` ? Enums.AmountType.Percentage : Enums.AmountType.Amount });
    };

    const isFormValid = values.fullName && (
        (values.amountType === Enums.AmountType.Percentage && values.payPercentage && String(values.payPercentage).trim() !== ``) ||
        (values.amountType === Enums.AmountType.Amount && values.payAmount && String(values.payAmount).trim() !== ``)
    );

    return (
        <form onSubmit={submitHandler} className="bg-white border border-neutral-300 rounded-xl p-4 flex flex-col gap-6 w-full">
            {/* Email (disabled) and Full Name (editable) */}
            <div className="flex flex-col gap-4 w-full">
                {/* Email Field - Disabled */}
                <TextField
                    type="email"
                    name="email"
                    label="Email*"
                    value={values.email}
                    disabled
                />

                {/* Full Name Field - Editable */}
                <TextField
                    type="text"
                    name="fullName"
                    label="Full Name*"
                    value={values.fullName}
                    onChange={changeHandler}
                    error={errors.fullName}
                />
            </div>

            {/* Radio Buttons and Input */}
            <div className="flex flex-col gap-4 w-full">
                {/* Radio Button Selection */}
                <div className="flex gap-4 items-center">
                    {/* Percentage Radio */}
                    <label className="flex gap-2 items-center cursor-pointer group">
                        <div className="relative">
                            <input
                                type="radio"
                                name="amountType"
                                value="payPercentage"
                                checked={values.amountType === Enums.AmountType.Percentage}
                                onChange={amountTypeChangeHandler}
                                className="sr-only peer"
                            />
                            <div className={classNames(
                                `w-5 h-5 rounded-xl border bg-white flex items-center justify-center flex-shrink-0 transition-colors peer-focus-visible:outline-focus-ring`,
                                {
                                    "border-neutral-950": values.amountType === Enums.AmountType.Percentage,
                                    "border-neutral-200 group-hover:border-neutral-300": values.amountType !== Enums.AmountType.Percentage
                                }
                            )}>
                                {values.amountType === Enums.AmountType.Percentage ? (
                                    <div className="w-2.5 h-2.5 rounded-full bg-neutral-950 transition-colors"></div>
                                ) : (
                                    <div className="w-2.5 h-2.5 rounded-full bg-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                )}
                            </div>
                        </div>
                        <span
                            className={classNames(
                                `text-base leading-6 tracking-[0.16px] transition-colors`,
                                {
                                    "text-neutral-950": values.amountType === Enums.AmountType.Percentage,
                                    "text-neutral-700 group-hover:text-neutral-950": values.amountType !== Enums.AmountType.Percentage
                                }
                            )}
                        >
                            Percentage
                        </span>
                    </label>

                    <span className="text-sm leading-5 text-neutral-950 tracking-[0.14px]">or</span>

                    {/* Amount Radio */}
                    <label className="flex gap-2 items-center cursor-pointer group">
                        <div className="relative">
                            <input
                                type="radio"
                                name="amountType"
                                value="payAmount"
                                checked={values.amountType === Enums.AmountType.Amount}
                                onChange={amountTypeChangeHandler}
                                className="sr-only peer"
                            />
                            <div className={classNames(
                                `w-5 h-5 rounded-xl border bg-white flex items-center justify-center flex-shrink-0 transition-colors peer-focus-visible:outline-focus-ring`,
                                {
                                    "border-neutral-950": values.amountType === Enums.AmountType.Amount,
                                    "border-neutral-200 group-hover:border-neutral-300": values.amountType !== Enums.AmountType.Amount
                                }
                            )}>
                                {values.amountType === Enums.AmountType.Amount ? (
                                    <div className="w-2.5 h-2.5 rounded-full bg-neutral-950 transition-colors"></div>
                                ) : (
                                    <div className="w-2.5 h-2.5 rounded-full bg-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                )}
                            </div>
                        </div>
                        <span
                            className={classNames(
                                `text-base leading-6 tracking-[0.16px] transition-colors`,
                                {
                                    "text-neutral-950": values.amountType === Enums.AmountType.Amount,
                                    "text-neutral-700 group-hover:text-neutral-950": values.amountType !== Enums.AmountType.Amount
                                }
                            )}
                        >
                            Amount
                        </span>
                    </label>
                </div>

                {/* Input Field */}
                {/* In each tab, fall back to showing an error from the other
                  * tab, so an error in the other isn't missed. */}
                <div>
                    {values.amountType === Enums.AmountType.Percentage ? (
                        <PercentInput
                            as={TextField}
                            name="payPercentage"
                            value={values.payPercentage}
                            onValueChange={(value) => changeAmountProxy({ target: { name: `payPercentage`, value } })}
                            placeholder="Enter percentage"
                            error={errors.payPercentage || errors.payAmount}
                        />
                    ) : (
                        <MoneyInput
                            as={TextField}
                            name="payAmount"
                            value={values.payAmount}
                            onValueChange={(value) => changeAmountProxy({ target: { name: `payAmount`, value } })}
                            placeholder="Enter amount"
                            error={errors.payAmount || errors.payPercentage}
                        />
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-row gap-4 w-full">
                <Button
                    variant="secondary"
                    size="lg"
                    className="flex-1"
                    onClick={cancelEditing}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="flex-1"
                    disabled={!isFormValid}
                >
                    Update Split
                </Button>
            </div>
        </form>
    );
};

export default UpsertPayor;
